import React, { useState } from 'react'
import { Button } from '@UILibrary/Button'
import { useCopyToClipboard } from '@hooks/useCopyToClipboard'

import { Enrolment } from '../api'
import { groupSetupKey, isSixDigitCode } from '../utils'
import styles from '../security-settings.module.sass'

type Props = {
  enrolment: Enrolment
  error: string | null
  isSubmitting: boolean
  onCancel: () => void
  onConfirm: (code: string) => void
}

export const EnrolmentPanel = ({ enrolment, error, isSubmitting, onCancel, onConfirm }: Props) => {
  const [code, setCode] = useState('')
  const [isKeyCopied, setIsKeyCopied] = useState(false)
  const [copy] = useCopyToClipboard()

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    onConfirm(code.replace(/\s/g, ''))
  }

  const onCopyKey = () => {
    copy(enrolment.secret)
    setIsKeyCopied(true)
  }

  const onCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value)

  // The QR is an SVG string built by RQRCode on the server, not user input.
  const qrCode = <div className={styles.qrCode} dangerouslySetInnerHTML={{ __html: enrolment.qrCodeSvg }} />

  const errorMessage = error ? <p className={styles.error} role="alert">{error}</p> : null
  const copyLabel = isKeyCopied ? 'Copied' : 'Copy key'

  return (
    <form className={`app-form ${styles.panel}`} onSubmit={onSubmit}>
      <ol className={styles.steps}>
        <li className={styles.step}>
          <div className={styles.stepBody}>
            <h3 className={styles.stepTitle}>Add Wealthbit to your authenticator app</h3>
            <p>
              In 1Password, Authy, Google Authenticator or any other authenticator app, choose to add
              an account and scan this QR code.
            </p>

            <div className={styles.scan}>
              {qrCode}

              <div className={styles.manualEntry}>
                <p className={styles.manualEntryHeading}>Can't scan it?</p>
                <p>Enter this setup key in your app instead, choosing a time-based code if asked.</p>
                <code className={styles.secret}>{groupSetupKey(enrolment.secret)}</code>
                <Button variant="secondary" size="small" type="button" onClick={onCopyKey}>{copyLabel}</Button>
              </div>
            </div>
          </div>
        </li>
        <li className={styles.step}>
          <div className={styles.stepBody}>
            <h3 className={styles.stepTitle}>Enter the code from your app</h3>
            <p>Your app now shows a 6-digit code for Wealthbit that changes every 30 seconds. Enter it here to finish.</p>
            <div className={`form-field app-form__field ${styles.codeField}`}>
              <label htmlFor="authenticator-code">6-digit code</label>
              <input
                id="authenticator-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                value={code}
                onChange={onCodeChange}
              />
            </div>
            {errorMessage}
          </div>
        </li>
      </ol>

      <div className={styles.actions}>
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" disabled={isSubmitting || !isSixDigitCode(code)}>
          Turn on authenticator app
        </Button>
      </div>
    </form>
  )
}
